import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { accountAPI, type AccountUser } from "@/lib/accountApi";
import { chatStorageAPI } from "@/lib/chatStorage";
import { trackAccountEvent } from "@/lib/analytics";
import { AuthModal } from "@/components/auth/AuthModal";

export type AuthModalMode = "login" | "signup" | "forgot";

/** What happened to the account, for listeners such as the chat list. */
export type AccountChange = "login" | "logout" | "deleted";

export type AuthActionResult =
  | { success: true }
  | { success: false; error: string; code?: string };

interface OpenAuthModalOptions {
  email?: string;
  // A note above the form, such as "E-Mail bestätigt".
  notice?: string;
  // The chat whose save card opened the modal, for analytics.
  chatId?: string;
}

interface AuthContextValue {
  /** False until /api/account/config says otherwise; nothing renders then. */
  accountsEnabled: boolean;
  isLoading: boolean;
  user: AccountUser | null;
  isAuthenticated: boolean;
  /** The address signed up in this browser and not confirmed yet. */
  pendingVerificationEmail: string | null;
  login: (email: string, password: string) => Promise<AuthActionResult>;
  register: (input: {
    email: string;
    password: string;
    firstName?: string;
  }) => Promise<AuthActionResult>;
  logout: () => Promise<AuthActionResult>;
  requestPasswordReset: (email: string) => Promise<AuthActionResult>;
  deleteAccount: () => Promise<AuthActionResult>;
  refresh: () => Promise<void>;
  openAuthModal: (mode: AuthModalMode, options?: OpenAuthModalOptions) => void;
  closeAuthModal: () => void;
  /** Calls `listener` after login, logout and account deletion. */
  subscribe: (listener: (change: AccountChange) => void) => () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface ModalState {
  open: boolean;
  mode: AuthModalMode;
  email?: string;
  notice?: string;
  chatId?: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accountsEnabled, setAccountsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<AccountUser | null>(null);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState<
    string | null
  >(null);
  const [modal, setModal] = useState<ModalState>({ open: false, mode: "login" });
  const listenersRef = useRef(new Set<(change: AccountChange) => void>());
  const modalChatIdRef = useRef<string | undefined>(undefined);

  const notify = useCallback((change: AccountChange) => {
    for (const listener of listenersRef.current) {
      listener(change);
    }
  }, []);

  const subscribe = useCallback((listener: (change: AccountChange) => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const refresh = useCallback(async () => {
    setUser(await accountAPI.me());
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { enabled } = await accountAPI.getConfig();
      if (cancelled) return;
      setAccountsEnabled(enabled);
      if (enabled) {
        const me = await accountAPI.me();
        if (!cancelled) setUser(me);
      }
      if (!cancelled) setIsLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthActionResult> => {
      const result = await accountAPI.login({
        email,
        password,
        // The backend moves this session's anonymous chats into the account.
        sessionId: chatStorageAPI.getSessionId(),
      });
      if (!result.ok) {
        return { success: false, error: result.detail, code: result.code };
      }
      setUser(result.data.user);
      setPendingVerificationEmail(null);
      trackAccountEvent("account_logged_in", {
        sessionId: chatStorageAPI.getSessionId(),
        chatId: modalChatIdRef.current,
        metadata: { claimed_chats: result.data.claimedChats },
      });
      notify("login");
      return { success: true };
    },
    [notify],
  );

  const register = useCallback(
    async (input: {
      email: string;
      password: string;
      firstName?: string;
    }): Promise<AuthActionResult> => {
      const result = await accountAPI.register({
        ...input,
        // Verifying the email moves this session's chats into the account.
        sessionId: chatStorageAPI.getSessionId(),
      });
      if (!result.ok) {
        return { success: false, error: result.detail, code: result.code };
      }
      setPendingVerificationEmail(input.email);
      trackAccountEvent("account_registered", {
        sessionId: chatStorageAPI.getSessionId(),
        chatId: modalChatIdRef.current,
      });
      return { success: true };
    },
    [],
  );

  // After logout or deletion the browser starts over with a new anonymous
  // session, so the next person on a shared device sees no chats.
  const endSession = useCallback(
    (change: AccountChange) => {
      setUser(null);
      setPendingVerificationEmail(null);
      chatStorageAPI.resetSession();
      notify(change);
    },
    [notify],
  );

  const logout = useCallback(async (): Promise<AuthActionResult> => {
    const result = await accountAPI.logout();
    if (!result.ok) {
      // If the cookie survived, the chat list would still show the account's
      // chats, so stay signed in and say so.
      const stillSignedIn = await accountAPI.me();
      if (stillSignedIn) {
        setUser(stillSignedIn);
        return { success: false, error: result.detail, code: result.code };
      }
    }
    endSession("logout");
    return { success: true };
  }, [endSession]);

  const deleteAccount = useCallback(async (): Promise<AuthActionResult> => {
    const result = await accountAPI.deleteAccount();
    if (!result.ok) {
      return { success: false, error: result.detail, code: result.code };
    }
    endSession("deleted");
    return { success: true };
  }, [endSession]);

  const requestPasswordReset = useCallback(
    async (email: string): Promise<AuthActionResult> => {
      const result = await accountAPI.requestPasswordReset(email);
      return result.ok
        ? { success: true }
        : { success: false, error: result.detail, code: result.code };
    },
    [],
  );

  const openAuthModal = useCallback(
    (mode: AuthModalMode, options: OpenAuthModalOptions = {}) => {
      modalChatIdRef.current = options.chatId;
      setModal({ open: true, mode, ...options });
    },
    [],
  );

  const closeAuthModal = useCallback(() => {
    modalChatIdRef.current = undefined;
    setModal((current) => ({ ...current, open: false }));
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      accountsEnabled,
      isLoading,
      user,
      isAuthenticated: user !== null,
      pendingVerificationEmail,
      login,
      register,
      logout,
      requestPasswordReset,
      deleteAccount,
      refresh,
      openAuthModal,
      closeAuthModal,
      subscribe,
    }),
    [
      accountsEnabled,
      isLoading,
      user,
      pendingVerificationEmail,
      login,
      register,
      logout,
      requestPasswordReset,
      deleteAccount,
      refresh,
      openAuthModal,
      closeAuthModal,
      subscribe,
    ],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {accountsEnabled && (
        <AuthModal
          isOpen={modal.open}
          mode={modal.mode}
          initialEmail={modal.email}
          notice={modal.notice}
          onClose={closeAuthModal}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
}
